import React, { useState, useEffect } from 'react';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';

import { useAuth } from '../../../../../../hooks/Auth';
import { FaQrcode } from 'react-icons/fa';
import { useModal } from '../../../../../../hooks/Modal';
import { formatReal } from '../../../../../../utils/format';
import { useSearch } from '../../../../../../hooks/Search';

interface ICreateModule {
  lineData: Record<string, any>;
}

const fieldStyle: React.CSSProperties = { height: 34, padding: '0 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', textAlign: 'right', minWidth: 160 };
const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '4px 0', color: '#333', fontSize: '14px' };

const Content = ({ lineData, onDone }) => {
  const { handleApiErrors } = useAuth();
  const { addToast } = useToast();

  // a linha da listagem não traz os valores crus: carrega a venda completa
  const [sale, setSale] = useState<Record<string, any> | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Record<string, any> | null>(null);

  const total = Number(sale?.total || 0);
  const fees = Number(sale?.fees || 0);

  const load = async () => {
    try {
      const response = await api.get(`/sales/${lineData?._id?.toString()}`);
      const item = response?.data || {};
      setSale(item);
      // valor sugerido: total sem os juros do parcelamento
      setValue(formatReal(Number(item?.total || 0) - Number(item?.fees || 0)));
    }
    catch (err) {
      addToast({ title: 'Erro ao carregar a venda', type: 'error' });
      handleApiErrors(err);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cents = parseInt(value.replace(/\D/g, '') || '0', 10);

  const handleSend = async (): Promise<void> => {
    if (cents < 100) {
      addToast({ title: 'Informe o valor do pix', type: 'error' });
      return;
    }

    setBusy(true);
    try {
      const response = await api.post(`/v4/create-sale-new-pix/${sale?.sale_hash}`, { amount: cents });

      setResult(response?.data);
      addToast({ title: 'Pix emitido com sucesso!', type: 'success' });
      onDone();
    }
    catch (err) {
      addToast({ title: err?.response?.data?.message || 'Erro ao emitir o pix', type: 'error' });
      handleApiErrors(err);
    }
    setBusy(false);
  };

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(result?.pix_qr_code || '');
      addToast({ title: 'Código pix copiado', type: 'success' });
    }
    catch (err) {
      addToast({ title: 'Não foi possível copiar, selecione o código manualmente', type: 'error' });
    }
  };

  if (result) {
    return <div className='column gap-xs' style={{ width: '100%' }}>
      <h2 style={{ color: '#333', fontSize: '16px', width: '100%', textAlign: 'left' }}>Pix emitido</h2>
      <div style={rowStyle}><span>Valor</span><strong>R$ {formatReal(result?.total)}</strong></div>
      <div style={rowStyle}><span>Ingressos copiados</span><strong>{result?.tickets || 0}</strong></div>
      <div style={rowStyle}><span>Nova venda</span><small>{result?.new_sale_hash}</small></div>
      <textarea readOnly value={result?.pix_qr_code || ''} onFocus={e => e.target.select()}
        style={{ width: '100%', minHeight: '90px', padding: '10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 12, color: '#333', fontFamily: 'monospace' }} />
      <button type='button' className='defaultButton' onClick={copyCode}>Copiar código pix</button>
    </div>
  }

  if (!sale) {
    return <p style={{ color: '#333', fontSize: '14px' }}>Carregando venda…</p>
  }

  return <div className='column gap-xs' style={{ width: '100%' }}>
    <h2 style={{ color: '#333', fontSize: '16px', width: '100%', textAlign: 'left' }}>Reemitir venda como pix</h2>
    <p style={{ color: '#333', fontSize: '14px', width: '100%', textAlign: 'left' }}>{sale?.client_id_response?.name}</p>

    <div style={rowStyle}><span>Forma atual</span><span>{sale?.payment_method_id} · {sale?.installment || 1}x · {sale?.payment_status_id}</span></div>
    <div style={rowStyle}><span>Total atual</span><span>R$ {formatReal(total)}</span></div>
    <div style={rowStyle}><span>Juros do parcelamento (fees)</span><span>- R$ {formatReal(fees)}</span></div>
    <div style={rowStyle}><span>Valor do pix (R$)</span>
      <input type="text" inputMode="numeric" autoComplete="off" placeholder="0,00" value={value} style={fieldStyle}
        onChange={e => { const digits = e.target.value.replace(/\D/g, '').replace(/^0+/, ''); setValue(digits ? formatReal(Number(digits)) : ''); }} />
    </div>

    <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', fontSize: '12px', width: '100%', textAlign: 'left' }}>
      Cria uma cópia da venda (novo sale_hash, à vista, sem juros e sem a taxa de R$ 0,99), copia os ingressos com novos códigos e emite o pix. O código pix é enviado por e-mail ao comprador. A venda original não é alterada.
    </p>

    <button type='button' disabled={busy} onClick={handleSend} className='defaultButton'>{busy ? 'Emitindo…' : 'Emitir pix'}</button>
  </div>
}

export default function UpdateModule({

  lineData,

}: ICreateModule): JSX.Element {
  const { addModal } = useModal();
  const { reloadSearchAll } = useSearch();

  const openIt = () => {

    addModal({
      theme: 'whiteModalMedium',
      title: '', key: 'modal', content: <Content lineData={lineData} onDone={() => reloadSearchAll()} />
    })
  }

  const moduleUpdate = (id: string): JSX.Element => (
    id ? <button className="lineIcon" title="Reemitir como pix" type="button" onClick={() => openIt()}>
      <FaQrcode />
    </button> : <></>
  );

  return moduleUpdate(lineData?.sale_hash);
}
